import { computed, nextTick, reactive } from 'vue';

/**
 * @typedef {Object} ActivityNavigationSource
 * The list the back/next buttons walk through. Only getRows is required; the
 * rest are capabilities a source may not have, defaulted in DEFAULT_SOURCE.
 *
 * @property {() => Array<{id: string, session?: string | null, startTimestamp?: Object}>} getRows
 *   Activities in display order. Must read through to the underlying reactive
 *   state on every call - a cached array would freeze navigation.
 *   startTimestamp orders activities within a session; rows without it keep their order.
 * @property {() => boolean} [isReady] False while the source cannot be reached
 *   yet, e.g. a table ref that has not been rendered.
 * @property {() => boolean} [canLoadMore]
 * @property {() => Promise<void>} [loadMore]
 * @property {(id: string) => void} [refreshRow]
 * @property {(id: string) => void} [revealRow]
 */

const MAX_EMPTY_BATCHES_PER_STEP = 5;

/** @type {ActivityNavigationSource} */
const DEFAULT_SOURCE = {
  getRows: () => [],
  isReady: () => true,
  canLoadMore: () => false,
  loadMore: async () => {},
  refreshRow: () => {},
  revealRow: () => {},
};

/**
 * Adapts a SessionActivitiesTable instance to ActivityNavigationSource.
 * getTable is called on every access: the ref only exists once the table has rendered.
 *
 * @param {() => Object|null} getTable
 * @property {(sessionId: string) => Promise<boolean>} [ensureSessionLoaded]
 * @property {(activityId: string) => Object|null} [getActivityById]
 * @returns {ActivityNavigationSource}
 */
export function sessionActivitiesTableSource(getTable) {
  return {
    isReady: () => !!getTable(),
    getRows: () => getTable()?.getNavigationActivities() ?? [],
    ensureSessionLoaded: (sessionId) => (
      getTable()?.loadNavigationActivitiesForSession(sessionId)
    ),
    getActivityById: (activityId) => (
      getTable()?.getNavigationActivityById(activityId) || null
    ),
    canLoadMore: () => !!getTable()?.canLoadMoreActivities(),
    loadMore: () => getTable()?.loadMoreActivities(),
    refreshRow: (id) => getTable()?.refresh(id),
    revealRow: (id) => getTable()?.revealActivity(id),
  };
}

/**
 * Drives back/next navigation for the session-activity dialogs: walks an ordered
 * list of activities, lazily fetching more rows at the end, and asks the host
 * page to open the right dialog for each target row.
 *
 * The walk goes over the rows grouped by session, not the raw table order - see
 * buildNavigationList.
 *
 * A dialog is only torn down when the target needs a different one: walking
 * between two activities of the same session, or between two standalone
 * activities, updates the mounted dialog in place.
 */
export function useActivityNavigation() {
  let source = DEFAULT_SOURCE;
  let openers = { activity: null, session: null };

  const state = reactive({
    currentId: null,
    isLoading: false,

    // Position at the last step, used when the row itself disappears — signing an
    // activity drops it out of Pending signatures right under the open dialog.
    lastIndex: -1,

    // Bumped when a source that was not ready becomes available; without it the
    // list computeds would cache their empty result with nothing to invalidate.
    sourceRevision: 0,
  });

  let currentKey = null;
  let currentDialog = null;
  let currentCloser = null;

  function typeOf(row) {
    return row && row.session ? 'session' : 'activity';
  }

  function keyOf(row) {
    return row && row.session ? `session:${row.session}` : 'activity';
  }

  function orderedList() {
    return state.sourceRevision >= 0 ? source.getRows() : [];
  }

  function startedAt(row) {
    return row.startTimestamp?.toMillis?.() ?? 0;
  }

  function endedAt(row) {
    return row.endTimestamp?.toMillis?.() ?? 0;
  }

  function compareRows(first, second) {
    const startDifference = startedAt(first) - startedAt(second);

    if (startDifference !== 0) {
      return startDifference;
    }

    const endDifference = endedAt(first) - endedAt(second);

    if (endDifference !== 0) {
      return endDifference;
    }

    const createDifference = (
      first.createTimestamp?.toMillis?.() ?? 0
    ) - (
      second.createTimestamp?.toMillis?.() ?? 0
    );

    if (createDifference !== 0) {
      return createDifference;
    }

    return String(first.id).localeCompare(String(second.id));
  }

  /**
   * The rows regrouped so all activities of one session sit together, in the order
   * the session card lists them.
   */
  function buildNavigationList() {
    const groups = [];
    const groupBySession = new Map();

    orderedList().forEach((row) => {
      if (!row.session) {
        groups.push([row]);
        return;
      }

      const group = groupBySession.get(row.session);

      if (group) {
        group.push(row);
        return;
      }

      const newGroup = [row];
      groupBySession.set(row.session, newGroup);
      groups.push(newGroup);
    });

    groups.forEach((group) => group.sort(compareRows));

    return groups.flat();
  }

  // Deep links configure navigation before $refs exist.
  function watchForSource(attempts = 3) {
    if (source.isReady() || attempts <= 0) {
      return;
    }
    nextTick(() => {
      state.sourceRevision += 1;
      watchForSource(attempts - 1);
    });
  }

  /**
   * Position of the current activity in the ordered list.
   *
   * When the row is gone - deleted, or edited so that it no longer passes the
   * list's filters - navigation continues from the slot it used to occupy:
   * whatever moved into that slot is the new "next", and the row before it
   * the new "back".
   *
   * @returns {{ index: number, present: boolean }}
   */
  function resolveIndex(list) {
    const index = list.findIndex((activity) => activity.id === state.currentId);
    if (index >= 0) {
      return { index, present: true };
    }
    const fallback = state.lastIndex;
    return {
      index: (fallback >= 0) && (fallback <= list.length) ? fallback : -1,
      present: false,
    };
  }

  const position = computed(() => resolveIndex(buildNavigationList()));

  const hasPrev = computed(() => position.value.index > 0);

  const hasNext = computed(() => {
    const { index, present } = position.value;
    const list = buildNavigationList();

    if (index < 0) {
      return false;
    }

    return (present ? index + 1 : index) < list.length;
  });

  let controller;

  // Closes the mounted dialog through the routine it registered, so its own cleanup runs.
  async function closeCurrentDialog() {
    const closer = currentCloser;
    const dialog = currentDialog;
    currentCloser = null;
    currentDialog = null;
    currentKey = null;

    if (closer) {
      await closer();
      return;
    }
    if (dialog && typeof dialog.hide === 'function') {
      dialog.hide();
    }
  }

  // Keeps the list showing the row we moved to, so closing the dialog does not drop the user back on page 1.
  function setCurrent(row) {
    state.currentId = row.id;

    // A position in the navigation order, not in the table's flat order.
    const index = buildNavigationList().findIndex((activity) => activity.id === row.id);
    if (index < 0) {
      return;
    }
    state.lastIndex = index;
    source.revealRow(row.id);
  }

  async function activate(row) {
    const nextKey = keyOf(row);

    if (currentDialog && nextKey === currentKey) {
      setCurrent(row);
      return;
    }

    // A different dialog is needed. Close the current one before moving,
    // otherwise it would react to the new id while unmounting.
    await closeCurrentDialog();

    if (row.session && source.ensureSessionLoaded) {
      try {
        await source.ensureSessionLoaded(row.session);
      } catch (err) {
        console.error(err);
      }
    }

    const canonicalRow = source.getActivityById?.(row.id) || row;

    setCurrent(canonicalRow);
    currentKey = nextKey;
    const opener = openers[typeOf(canonicalRow)];
    // A deep link carries no activity id: nothing to walk through, no arrows.
    const rowController = canonicalRow.id ? controller : null;
    currentDialog = (opener && await opener(canonicalRow, rowController)) || null;
  }

  function targetIndex(list, delta) {
    const { index, present } = resolveIndex(list);
    if (index < 0) {
      return -1;
    }

    if (present) {
      return index + delta;
    }

    return delta > 0 ? index : index - 1;
  }

  /**
   * The row one place away, fetching more rows when the step runs past what is loaded.
   * The position is resolved again after every fetch: a new batch can add activities
   * to a session that already has a group, shifting every position after it, and a
   * batch can be filtered out whole by the list's client-side filters.
   */
  async function resolveTarget(delta) {
    let emptyBatches = 0;

    while (emptyBatches < MAX_EMPTY_BATCHES_PER_STEP) {
      const list = buildNavigationList();
      const targetIdx = targetIndex(list, delta);
      if (targetIdx < 0) {
        return null;
      }
      if (targetIdx < list.length) {
        return list[targetIdx];
      }

      if (!source.canLoadMore()) {
        return null;
      }

      const sizeBefore = list.length;
      state.isLoading = true;
      try {
        // Sequential on purpose: each batch continues from the previous one.
        // eslint-disable-next-line no-await-in-loop
        await source.loadMore();
      } finally {
        state.isLoading = false;
      }

      emptyBatches = buildNavigationList().length > sizeBefore ? 0 : emptyBatches + 1;
    }

    return null;
  }

  async function go(delta) {
    const target = await resolveTarget(delta);
    if (target) {
      await activate(target);
    }
  }

  async function safeGo(delta) {
    try {
      await go(delta);
    } catch (err) {
      console.error(err);
    }
  }

  controller = reactive({
    currentId: computed(() => state.currentId),
    isLoading: computed(() => state.isLoading),
    hasPrev,
    hasNext,
    goPrev: () => safeGo(-1),
    goNext: () => safeGo(1),

    setCurrent: (row) => {
      if (row) {
        setCurrent(row);
      }
    },

    peek: (delta) => {
      const list = buildNavigationList();
      const targetIdx = targetIndex(list, delta);
      return targetIdx < 0 ? null : (list[targetIdx] || null);
    },

    registerCloser: (close) => {
      currentCloser = close;
    },
    unregisterCloser: (close) => {
      if (currentCloser === close) {
        currentCloser = null;
        currentDialog = null;
        currentKey = null;
      }
    },

    refreshRow: (id) => source.refreshRow(id),
  });

  return {
    controller,

    /**
     * Wire the composable to its host. Call once (e.g. in created).
     * @param {{ source?: ActivityNavigationSource, openers?: Object }} options
     */
    configure(options) {
      if (options.source) {
        source = { ...DEFAULT_SOURCE, ...options.source };
      }
      openers = { ...openers, ...(options.openers || {}) };
    },

    start(row) {
      if (!row) {
        return Promise.resolve();
      }

      currentKey = null;
      currentDialog = null;
      currentCloser = null;
      state.currentId = null;
      state.lastIndex = -1;
      watchForSource();

      return activate(row).catch((err) => {
        console.error(err);
      });
    },
  };
}
